import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Lock, Loader2, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/15 rounded-sm px-4 py-3 text-base focus:border-accent focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent mb-8 transition-colors">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="border border-white/10 rounded-sm p-8 md:p-10 bg-surface inner-glow">
          <div className="grid place-items-center h-12 w-12 rounded-full border border-white/15 text-accent mb-6">
            <Lock size={20} />
          </div>
          <div className="font-display text-3xl tracking-[0.2em] mb-1">LEYLAK<span className="text-accent">.</span></div>
          <p className="text-sm text-muted-foreground mb-8">Admin dashboard access</p>

          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <input
              data-testid="admin-email"
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
            <input
              data-testid="admin-password"
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground py-3 font-medium disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : null}
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
